import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";

const ErrorContext = createContext(null);

function id() {
  return Math.random().toString(36).slice(2, 9);
}

const initial = { currentError: null, alerts: [] };

function reducer(state, action) {
  switch (action.type) {
    case "SET_ERROR":
      return { ...state, currentError: action.payload };
    case "CLEAR_ERROR":
      return { ...state, currentError: null };
    case "SET_ALERT":
      return { ...state, alerts: [...state.alerts, action.payload] };
    case "POP_ALERT":
      return {
        ...state,
        alerts: state.alerts.filter((a) => a.id !== action.payload),
      };
    default:
      return state;
  }
}

export function ErrorProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initial);

  const setError = useCallback(
    (msg) => dispatch({ type: "SET_ERROR", payload: msg }),
    []
  );
  const clearError = useCallback(() => dispatch({ type: "CLEAR_ERROR" }), []);
  const setAlert = useCallback((a) => {
    dispatch({
      type: "SET_ALERT",
      payload: { id: id(), severity: "info", autoHideMs: 4000, ...a },
    });
  }, []);
  const popAlert = useCallback(
    (alertId) => dispatch({ type: "POP_ALERT", payload: alertId }),
    []
  );

  const value = useMemo(
    () => ({
      error: state.currentError,
      setError,
      clearError,
      setAlert,
      popAlert,
      alerts: state.alerts,
    }),
    [state.currentError, state.alerts, setError, clearError, setAlert, popAlert]
  );

  return (
    <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>
  );
}

export function useError() {
  const ctx = useContext(ErrorContext);
  if (!ctx) throw new Error("useError must be used within <ErrorProvider>");
  return ctx;
}
